import { useState, useEffect } from 'react';
import { api } from '../api';

function gradeFor(obtained, max) {
  if (!obtained || !max) return '';
  const pct = (obtained / max) * 100;
  if (pct >= 90) return 'A+';
  if (pct >= 75) return 'A';
  if (pct >= 60) return 'B';
  if (pct >= 45) return 'C';
  if (pct >= 35) return 'D';
  return 'F';
}

function gradeClass(g) {
  if (!g) return '';
  if (g.startsWith('A')) return 'grade grade-a';
  if (g === 'B') return 'grade grade-b';
  if (g === 'C') return 'grade grade-c';
  if (g === 'D') return 'grade grade-c';
  return 'grade grade-f';
}

function fullName(s) { return s ? `${s.firstName || ''} ${s.lastName || ''}`.trim() : ''; }
function className(c) { return c ? `${c.name} - ${c.section}` : '—'; }

export default function Marks() {
  const [tab, setTab] = useState('entry');
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [examObj, setExamObj] = useState(null);
  const [saved, setSaved] = useState(false);

  // New exam form
  const [newExam, setNewExam] = useState({
    name: '', examType: '', academicYear: '2024-25',
    startDate: '', endDate: '', maxMarks: 100, passMarks: 35
  });

  useEffect(() => {
    Promise.all([api.getClasses(), api.getSubjects(), api.getExams()])
      .then(([c, s, e]) => { setClasses(c); setSubjects(s); setExams(e); });
  }, []);

  useEffect(() => {
    if (!selectedClass) { setStudents([]); return; }
    api.getStudentsByClass(selectedClass).then(setStudents);
  }, [selectedClass]);

  useEffect(() => {
    if (!selectedExam) { setMarks({}); setExamObj(null); return; }
    const e = exams.find(x => x.id === parseInt(selectedExam));
    setExamObj(e);
    api.getMarksByExam(selectedExam).then(list => {
      const m = {};
      list.forEach(mk => m[mk.student.id] = mk.marks);
      setMarks(m);
    });
  }, [selectedExam, exams]);

  const handleSaveMarks = async () => {
    if (!selectedExam || !selectedClass || !selectedSubject) {
      alert('Please select class, exam and subject'); return;
    }
    const payload = students.map(s => ({
      exam: { id: parseInt(selectedExam) },
      student: { id: s.id },
      classSection: { id: parseInt(selectedClass) },
      subject: { id: parseInt(selectedSubject) },
      marks: parseFloat(marks[s.id] || 0),
      grade: gradeFor(marks[s.id], examObj?.maxMarks)
    }));
    try {
      await api.saveMarksBulk(payload);
      window.location.reload();
    } catch (e) { alert('Save failed: ' + e.message); }
  };

  const handleCreateExam = async () => {
    if (!newExam.name) { alert('Exam name is required'); return; }
    try {
      await api.createExam(newExam);
      window.location.reload();
    } catch (e) { alert('Failed to create exam: ' + e.message); }
  };

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'entry' ? ' active' : ''}`} onClick={() => setTab('entry')}>Marks Entry</button>
        <button className={`tab${tab === 'exam' ? ' active' : ''}`} onClick={() => setTab('exam')}>Manage Exams</button>
        <button className={`tab${tab === 'report' ? ' active' : ''}`} onClick={() => setTab('report')}>Result Report</button>
      </div>

      {tab === 'entry' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Select Class, Exam &amp; Subject</div>
            </div>
            <div className="card-body">
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="form-label">Class</label>
                  <select className="form-control" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
                    <option value="">-- Select Class --</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{className(c)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Exam</label>
                  <select className="form-control" value={selectedExam} onChange={e => setSelectedExam(e.target.value)}>
                    <option value="">-- Select Exam --</option>
                    {exams.map(e => <option key={e.id} value={e.id}>{e.name} ({e.academicYear})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <select className="form-control" value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                    <option value="">-- Select Subject --</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {examObj && students.length > 0 && selectedSubject && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">
                  {examObj.name} — {subjects.find(s => s.id === parseInt(selectedSubject))?.name}
                </div>
                <div className="card-subtitle">Max marks: {examObj.maxMarks}</div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>#</th><th>Student</th><th>Admission No</th><th>Marks Obtained</th><th>Grade</th></tr>
                  </thead>
                  <tbody>
                    {students.map((s, i) => {
                      const m = marks[s.id] ?? '';
                      const g = gradeFor(m, examObj.maxMarks);
                      return (
                        <tr key={s.id}>
                          <td>{i + 1}</td>
                          <td>{fullName(s)}</td>
                          <td>{s.admissionNo}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input className="form-control" type="number" min={0} max={examObj.maxMarks}
                                value={m}
                                onChange={e => setMarks(prev => ({ ...prev, [s.id]: e.target.value }))}
                                style={{ width: 90 }} />
                              <span style={{ color: '#888', fontSize: 12 }}>/ {examObj.maxMarks}</span>
                            </div>
                          </td>
                          <td><span className={gradeClass(g)}>{g}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button className="btn btn-success" onClick={handleSaveMarks}>
                  <i className="fas fa-save"></i> Save Marks
                </button>
                {saved && <span className="badge badge-green">Saved!</span>}
              </div>
            </div>
          )}

          {selectedClass && students.length === 0 && (
            <div className="card"><div className="card-body" style={{ color: '#888', textAlign: 'center' }}>No students in this class</div></div>
          )}
        </div>
      )}

      {tab === 'exam' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Create New Exam</div>
          </div>
          <div className="card-body">
            <div className="form-grid cols-3">
              <div className="form-group">
                <label className="form-label">Exam Name *</label>
                <input className="form-control" value={newExam.name} onChange={e => setNewExam(f => ({ ...f, name: e.target.value }))} placeholder="Mid Term, Final Exam…" />
              </div>
              <div className="form-group">
                <label className="form-label">Exam Type</label>
                <select className="form-control" value={newExam.examType} onChange={e => setNewExam(f => ({ ...f, examType: e.target.value }))}>
                  <option value="">-- Select --</option>
                  <option value="Unit Test">Unit Test</option>
                  <option value="Mid Term">Mid Term</option>
                  <option value="Final">Final</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Half Yearly">Half Yearly</option>
                  <option value="Annual">Annual</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Academic Year</label>
                <input className="form-control" value={newExam.academicYear} onChange={e => setNewExam(f => ({ ...f, academicYear: e.target.value }))} placeholder="2024-25" />
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input className="form-control" type="date" value={newExam.startDate} onChange={e => setNewExam(f => ({ ...f, startDate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-control" type="date" value={newExam.endDate} onChange={e => setNewExam(f => ({ ...f, endDate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Max Marks</label>
                <input className="form-control" type="number" value={newExam.maxMarks} onChange={e => setNewExam(f => ({ ...f, maxMarks: parseInt(e.target.value) }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Pass Marks</label>
                <input className="form-control" type="number" value={newExam.passMarks} onChange={e => setNewExam(f => ({ ...f, passMarks: parseInt(e.target.value) }))} />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleCreateExam}>
                <i className="fas fa-plus"></i> Create Exam
              </button>
            </div>

            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12 }}>All Exams</div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>#</th><th>Name</th><th>Type</th><th>Year</th><th>Dates</th><th>Max / Pass</th></tr>
                  </thead>
                  <tbody>
                    {exams.length === 0 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 20 }}>No exams created yet</td></tr>
                    )}
                    {exams.map((e, i) => (
                      <tr key={e.id}>
                        <td>{i + 1}</td>
                        <td><strong>{e.name}</strong></td>
                        <td>{e.examType}</td>
                        <td>{e.academicYear}</td>
                        <td style={{ fontSize: 12 }}>{e.startDate} → {e.endDate}</td>
                        <td>{e.maxMarks} / {e.passMarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'report' && <ResultReport classes={classes} exams={exams} subjects={subjects} />}
    </div>
  );
}

function ResultReport({ classes, exams, subjects }) {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!selectedExam) { setResults([]); return; }
    api.getMarksByExam(selectedExam).then(list => {
      const filtered = selectedClass
        ? list.filter(r => r.classSection?.id === parseInt(selectedClass))
        : list;
      const sorted = [...filtered].sort((a, b) => b.marks - a.marks);
      setResults(sorted);
    });
  }, [selectedExam, selectedClass]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Result Report</div>
      </div>
      <div className="card-body">
        <div className="form-grid cols-3" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label className="form-label">Filter by Class (optional)</label>
            <select className="form-control" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
              <option value="">All Classes</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} - {c.section}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Exam</label>
            <select className="form-control" value={selectedExam} onChange={e => setSelectedExam(e.target.value)}>
              <option value="">-- Select Exam --</option>
              {exams.map(e => <option key={e.id} value={e.id}>{e.name} ({e.academicYear})</option>)}
            </select>
          </div>
        </div>
        {results.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Rank</th><th>Name</th><th>Adm No</th><th>Subject</th><th>Marks</th><th>Grade</th><th>Result</th></tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={r.id}>
                    <td><strong>#{i + 1}</strong></td>
                    <td>{fullName(r.student)}</td>
                    <td>{r.student?.admissionNo}</td>
                    <td>{r.subject?.name}</td>
                    <td>{r.marks} / {r.exam?.maxMarks}</td>
                    <td><span className={gradeClass(r.grade)}>{r.grade}</span></td>
                    <td>
                      <span className={`badge ${r.marks >= r.exam?.passMarks ? 'badge-green' : 'badge-red'}`}>
                        {r.marks >= r.exam?.passMarks ? 'PASS' : 'FAIL'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
